/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { CaretDownIcon } from '@radix-ui/react-icons'
import * as Popover from '@radix-ui/react-popover'
import React from 'react'

import { cn } from '@/utils/css'

/** The properties for the mobile header top bar popover. */
type MobileHeaderTopBarTextPopoverProps = {
  /** The title of the popover. */
  title: string

  /** The content to display in the popover. */
  children: React.ReactNode
}

/**
 * The popover for the top bar of the mobile header. Exposes a trigger that displays the popover content when clicked.
 */
const MobileHeaderTopBarTextPopover: React.FC<MobileHeaderTopBarTextPopoverProps> = ({ title, children }) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <span
        className={'group relative flex flex-row items-center gap-0.5 cursor-pointer hover:underline'}
        aria-label={`Expand ${title}`}
      >
        {title}
        <CaretDownIcon
          className={'relative top-px transition-transform duration-300 ease-in group-data-[state=open]:-rotate-180'}
        />
      </span>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content
        className={cn(
          'shadow-lg rounded-bl-2xl rounded-br-2xl p-2 bg-primary-500 will-change-[transform,opacity] focus:outline-hidden focus-visible:outline-hidden',
          'data-[state=open]:data-[side=top]:animate-slide-down-and-fade data-[state=open]:data-[side=right]:animate-slide-left-and-fade data-[state=open]:data-[side=bottom]:animate-slide-up-and-fade data-[state=open]:data-[side=left]:animate-slide-right-and-fade',
        )}
        sideOffset={10}
      >
        {children}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
)

export { MobileHeaderTopBarTextPopover }
